/*
  purist-bundle-offers.js  —  2026-09-23 native cart hook

  Bundle logic (pricing, roles, properties, add-to-cart payload, rogue-line cleanup) is UNCHANGED.
  Only three things were touched, all about what happens AFTER the bundle is in the cart:
    1. openCartDrawer(): if no OCU drawer/trigger is on the page, open the theme's own
       cart drawer through window.PuristCartDrawer.refreshAndOpen() (assets/cart-drawer.js).
       With OCU installed the old OCU path runs exactly as before.
    2. dispatchCartEvents(): additionally fires `purist:cart:refresh` so the native drawer
       re-renders after the delayed rogue-line cleanup.
    3. renderCartSections(): comment only (behaviour identical).
  2026-09-24 price alignment:
    4. getOfferMath(): the compare total sums regular prices instead of variant compare-at prices,
       so the struck total / line strikethrough / Save match the cart exactly.
    5. Every bundle line is added with _purist_bundle_regular_price, _purist_bundle_gift_value and
       _purist_bundle_shipping_value (private properties) so the cart drawer shows the card's numbers.
*/
class PuristBundleOffers extends HTMLElement {
  connectedCallback() {
    if (this.dataset.puristInitialized === 'true') return;
    this.dataset.puristInitialized = 'true';

    this.cards = Array.from(this.querySelectorAll('[data-purist-offer]'));
    this.message = this.querySelector('[data-purist-message]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.cartSections = (this.dataset.cartSections || 'cart-icon-bubble')
      .split(',')
      .map((section) => section.trim())
      .filter(Boolean)
      .filter((section) => !/cart-drawer|mini-cart|cart-notification/i.test(section))
      .slice(0, 5);

    this.adding = false;
    this.lastAtcInterceptAt = 0;
    this.lastAtcTrigger = null;
    this.bindFormsTimer = null;
    this.formObserver = null;

    document.documentElement.classList.add('purist-bundle-active');
    document.body?.classList.add('purist-bundle-active');
    this.installNativeCartAddGuard();
    this.disableNativeStickyAtc();
    setTimeout(() => this.disableNativeStickyAtc(), 100);
    setTimeout(() => this.disableNativeStickyAtc(), 600);
    setTimeout(() => this.disableNativeStickyAtc(), 1500);

    this.boundDocumentClick = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentTouchStart = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentTouchEnd = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentPointerDown = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentPointerUp = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentMouseDown = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentKeyDown = this.handleDocumentAtcEvent.bind(this);
    this.boundDocumentSubmit = this.handleDocumentSubmit.bind(this);

    this.cards.forEach((card) => {
      this.applyDefaultVariantMode(card);

      card.querySelector('[data-purist-select-offer]')?.addEventListener('click', () => this.toggleCard(card));
      card.querySelectorAll('[data-purist-variant-select]').forEach((select) => {
        this.updateItemDisplay(select);
        select.addEventListener('change', () => {
          this.updateItemDisplay(select);
          this.updateCardTotals(card);
        });
      });
      this.updateCardTotals(card);
    });

    const defaultCard = this.cards.find((card) => card.classList.contains('is-selected'));

if (defaultCard) {
  this.selectCard(defaultCard, { silent: true });
} else {
  this.cards.forEach((card) => {
    card.classList.remove('is-selected');
    card.querySelector('[data-purist-body]')?.toggleAttribute('hidden', true);
    card.querySelector('[data-purist-select-offer]')?.setAttribute('aria-expanded', 'false');
  });
}

    if (this.dataset.interceptAtc !== 'false') {
      this.bindProductForms();
      this.startDynamicFormObserver();

      /*
        Mobile sticky ATC can add the original page product on pointerdown/touchstart
        before click/touchend fires. Intercept the earliest events in capture mode.
      */
      document.addEventListener('touchstart', this.boundDocumentTouchStart, { capture: true, passive: false });
      document.addEventListener('pointerdown', this.boundDocumentPointerDown, true);
      document.addEventListener('mousedown', this.boundDocumentMouseDown, true);
      document.addEventListener('click', this.boundDocumentClick, true);
      document.addEventListener('touchend', this.boundDocumentTouchEnd, { capture: true, passive: false });
      document.addEventListener('pointerup', this.boundDocumentPointerUp, true);
      document.addEventListener('keydown', this.boundDocumentKeyDown, true);
      document.addEventListener('submit', this.boundDocumentSubmit, true);

      setTimeout(() => this.bindProductForms(), 100);
      setTimeout(() => this.bindProductForms(), 400);
      setTimeout(() => this.bindProductForms(), 1200);
      setTimeout(() => this.bindProductForms(), 2500);
    }
  }

  disconnectedCallback() {
    if (this.boundDocumentTouchStart) document.removeEventListener('touchstart', this.boundDocumentTouchStart, true);
    if (this.boundDocumentPointerDown) document.removeEventListener('pointerdown', this.boundDocumentPointerDown, true);
    if (this.boundDocumentMouseDown) document.removeEventListener('mousedown', this.boundDocumentMouseDown, true);
    if (this.boundDocumentClick) document.removeEventListener('click', this.boundDocumentClick, true);
    if (this.boundDocumentTouchEnd) document.removeEventListener('touchend', this.boundDocumentTouchEnd, true);
    if (this.boundDocumentPointerUp) document.removeEventListener('pointerup', this.boundDocumentPointerUp, true);
    if (this.boundDocumentKeyDown) document.removeEventListener('keydown', this.boundDocumentKeyDown, true);
    if (this.boundDocumentSubmit) document.removeEventListener('submit', this.boundDocumentSubmit, true);
    if (this.formObserver) this.formObserver.disconnect();
    if (this.bindFormsTimer) clearTimeout(this.bindFormsTimer);
  }

  getScope() {
    return this.closest('[data-section-type="product-page"], .m-main-product, .shopify-section') || document;
  }

  stickyAtcSelector() {
    return [
      '.m-product-sticky-atc',
      '.m-sticky-addtocart',
      '.m-sticky-add-to-cart',
      '.sticky-add-to-cart',
      '.sticky-atc',
      '.product-sticky-atc',
      '[data-sticky-add-to-cart]',
      '[data-mobile-add-to-cart]',
      '[data-sticky-atc]'
    ].join(',');
  }

  atcTriggerSelector() {
    return [
      '[data-purist-bundle-atc="true"]',
      'button[name="add"]',
      '[name="add"]',
      '.m-add-to-cart',
      '.m-product-form__submit',
      '.product-form__submit',
      '.product__submit',
      '[data-add-to-cart]',
      '[data-atc-button]',
      '[data-product-submit]',
      '[data-product-form-submit]',
      '[data-product-add-to-cart]',
      `${this.stickyAtcSelector()} button`,
      `${this.stickyAtcSelector()} a`,
      `${this.stickyAtcSelector()} [role="button"]`
    ].join(',');
  }

  isInScope(element) {
    if (!element) return false;

    const scope = this.getScope();
    if (scope === document || scope.contains(element)) return true;

    /*
      Mobile sticky ATC buttons are commonly rendered outside the product section.
      They still add the same original product unless we intercept them globally.
    */
    if (element.closest && element.closest(this.stickyAtcSelector())) return true;

    const form = this.findRelatedProductForm(element);
    return Boolean(form && (scope.contains(form) || form.dataset.puristBundleForm === 'true'));
  }

  shouldBindForm(form) {
    if (!form || !form.matches('form[action*="/cart/add"]')) return false;

    const scope = this.getScope();
    if (scope === document || scope.contains(form)) return true;
    if (form.closest(this.stickyAtcSelector())) return true;

    /*
      Some mobile themes keep the sticky button outside the form and only keep
      one real product form on the page. Marking that form lets the sticky button
      use it as the fallback form.
    */
    const productSection = form.closest('[data-section-type="product-page"], .m-main-product, .shopify-section');
    return Boolean(productSection && productSection.contains(this));
  }

  bindProductForms() {
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));

    forms.forEach((form) => {
      if (!this.shouldBindForm(form)) return;

      form.dataset.puristBundleForm = 'true';

      const addButtons = form.querySelectorAll([
        '[name="add"]',
        '.m-add-to-cart',
        '.m-product-form__submit',
        '.product-form__submit',
        '.product__submit',
        'button[type="submit"]',
        '[data-add-to-cart]',
        '[data-atc-button]',
        '[data-product-submit]',
        '[data-product-form-submit]',
        '[data-product-add-to-cart]'
      ].join(','));

      addButtons.forEach((button) => {
        if (this.isExcludedAtcTrigger(button)) return;

        if (button.tagName === 'BUTTON') button.setAttribute('type', 'button');
        button.setAttribute('data-purist-bundle-atc', 'true');

        if (button.dataset.puristBundleClickBound === 'true') return;
        button.dataset.puristBundleClickBound = 'true';

        button.addEventListener(
          'click',
          (event) => {
            this.stopNativeAtc(event);
            if (this.isDuplicateAtcEvent(button)) return;
            this.handleBundleAtc(form);
          },
          true
        );

        button.addEventListener(
          'touchend',
          (event) => {
            this.stopNativeAtc(event);
            if (this.isDuplicateAtcEvent(button)) return;
            this.handleBundleAtc(form);
          },
          true
        );
      });

      if (form.dataset.puristBundleSubmitBound === 'true') return;
      form.dataset.puristBundleSubmitBound = 'true';

      form.addEventListener(
        'submit',
        (event) => {
          this.stopNativeAtc(event);
          if (this.isDuplicateAtcEvent(form)) return;
          this.handleBundleAtc(form);
        },
        true
      );
    });

    this.bindStickyAtcTriggers();
  }

  bindStickyAtcTriggers() {
    document.querySelectorAll(this.atcTriggerSelector()).forEach((trigger) => {
      if (!(trigger instanceof Element)) return;
      if (trigger.dataset.puristBundleAtc === 'true') return;
      if (!trigger.closest(this.stickyAtcSelector())) return;
      if (this.isExcludedAtcTrigger(trigger)) return;

      trigger.setAttribute('data-purist-bundle-atc', 'true');
      if (trigger.tagName === 'BUTTON') trigger.setAttribute('type', 'button');
    });
  }

  startDynamicFormObserver() {
    if (this.formObserver || !document.documentElement) return;

    this.formObserver = new MutationObserver(() => {
      clearTimeout(this.bindFormsTimer);
      this.bindFormsTimer = setTimeout(() => {
        this.bindProductForms();
        this.disableNativeStickyAtc();
      }, 50);
    });

    this.formObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  findAtcTrigger(target) {
    if (!(target instanceof Element)) return null;

    const trigger = target.closest(this.atcTriggerSelector());
    if (!trigger) return null;
    if (this.isExcludedAtcTrigger(trigger)) return null;

    return trigger;
  }

  isExcludedAtcTrigger(trigger) {
    if (!trigger) return true;

    const label = [
      trigger.getAttribute('aria-label'),
      trigger.getAttribute('name'),
      trigger.getAttribute('type'),
      trigger.getAttribute('data-testid'),
      trigger.textContent,
      typeof trigger.className === 'string' ? trigger.className : ''
    ].filter(Boolean).join(' ');

    if (/shopify-payment-button|shopify-payment|dynamic-checkout/i.test(label)) return true;
    if (/buy\s*now|shop\s*pay|paypal|google\s*pay|apple\s*pay|checkout/i.test(label)) return true;
    if (trigger.closest && trigger.closest('.shopify-payment-button, [data-shopify="payment-button"]')) return true;

    return false;
  }

  findRelatedProductForm(trigger) {
    if (!trigger) return null;

    if (trigger.form && trigger.form.matches && trigger.form.matches('form[action*="/cart/add"]')) {
      return trigger.form;
    }

    const formId = trigger.getAttribute && trigger.getAttribute('form');
    if (formId) {
      const formById = document.getElementById(formId);
      if (formById && formById.matches('form[action*="/cart/add"]')) return formById;
    }

    const closestForm = trigger.closest && trigger.closest('form[action*="/cart/add"]');
    if (closestForm) return closestForm;

    const scope = this.getScope();
    const scopedForm = scope.querySelector && scope.querySelector('form[data-purist-bundle-form="true"], form[action*="/cart/add"]');
    if (scopedForm) return scopedForm;

    return document.querySelector('form[data-purist-bundle-form="true"], form[action*="/cart/add"]');
  }

  isDuplicateAtcEvent(trigger) {
    const now = Date.now();
    const previous = this.lastAtcTrigger;
    const duplicate =
      previous &&
      now - this.lastAtcInterceptAt < 850 &&
      (previous === trigger || previous.contains?.(trigger) || trigger.contains?.(previous));

    this.lastAtcInterceptAt = now;
    this.lastAtcTrigger = trigger;

    return duplicate;
  }


  getPageProductId() {
    const candidates = [
      document.body?.dataset?.productId,
      this.getScope()?.dataset?.productId,
      this.closest('[data-product-id]')?.dataset?.productId,
      document.querySelector('[data-section-type="product-page"][data-product-id]')?.dataset?.productId,
      document.querySelector('.m-main-product[data-product-id]')?.dataset?.productId
    ];

    for (const candidate of candidates) {
      const value = Number(candidate || 0);
      if (value > 0) return value;
    }

    return 0;
  }

  getPageVariantIds() {
    const ids = new Set();
    const pageProductId = this.getPageProductId();

    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      const formProductId = Number(form.dataset.productId || 0);
      if (pageProductId && formProductId && formProductId !== pageProductId) return;

      form.querySelectorAll('[name="id"]').forEach((field) => {
        if (field.matches('select')) {
          Array.from(field.options || []).forEach((option) => {
            const id = Number(option.value || 0);
            if (id > 0) ids.add(id);
          });
          return;
        }

        const id = Number(field.value || 0);
        if (id > 0) ids.add(id);
      });
    });

    return ids;
  }

  disableNativeStickyAtc() {
    const stickySelectors = [
      '.m-sticky-addtocart',
      '.m-product-sticky-atc',
      '.m-sticky-add-to-cart',
      '.sticky-add-to-cart',
      '.sticky-atc',
      '.product-sticky-atc',
      '[data-sticky-add-to-cart]',
      '[data-mobile-add-to-cart]',
      '[data-sticky-atc]'
    ];

    stickySelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((sticky) => {
        if (!(sticky instanceof Element)) return;

        sticky.setAttribute('data-purist-native-sticky-hidden', 'true');
        sticky.setAttribute('aria-hidden', 'true');
        sticky.style.setProperty('display', 'none', 'important');
        sticky.style.setProperty('pointer-events', 'none', 'important');

        sticky.querySelectorAll('button[name="add"], .m-add-to-cart, [data-add-to-cart], [data-product-add-to-cart]').forEach((button) => {
          if (button.tagName === 'BUTTON') button.setAttribute('type', 'button');
          button.setAttribute('data-purist-native-sticky-blocked', 'true');
        });
      });
    });
  }

  installNativeCartAddGuard() {
    if (window.PuristNativeCartAddGuardInstalled) return;
    window.PuristNativeCartAddGuardInstalled = true;

    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = function puristGuardedFetch(input, init = {}) {
      const bundleEl = document.querySelector('purist-bundle-offers[data-purist-initialized="true"], purist-bundle-offers');

      if (
        bundleEl &&
        typeof bundleEl.shouldBlockNativeCartAdd === 'function' &&
        bundleEl.shouldBlockNativeCartAdd(input, init)
      ) {
        const relatedForm = bundleEl.findRelatedProductForm?.(document.activeElement) || document.querySelector('form[data-purist-bundle-form="true"], form[action*="/cart/add"]');

        if (!bundleEl.adding && typeof bundleEl.handleBundleAtc === 'function') {
          setTimeout(() => bundleEl.handleBundleAtc(relatedForm), 0);
        }

        if (typeof Response !== 'undefined') {
          return Promise.resolve(
            new Response(JSON.stringify({ purist_blocked_native_product_add: true }), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            })
          );
        }
      }

      return originalFetch.apply(this, arguments);
    };
  }

  urlFromFetchInput(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  isCartAddUrl(url) {
    return /\/cart\/add(?:\.js)?(?:[?#].*)?$/i.test(String(url || ''));
  }

  bodyContainsBundleProperty(body) {
    if (!body) return false;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      for (const [key, value] of body.entries()) {
        if (String(key).includes('_purist_bundle_id') || String(value).includes('_purist_bundle_id')) return true;
      }
      return false;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body.toString().includes('_purist_bundle_id');
    }

    if (typeof body === 'string') {
      return body.includes('_purist_bundle_id') || decodeURIComponent(body).includes('_purist_bundle_id');
    }

    if (typeof body === 'object') {
      try {
        return JSON.stringify(body).includes('_purist_bundle_id');
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  extractVariantIdsFromCartAddBody(body) {
    const ids = new Set();
    if (!body) return ids;

    const addId = (value) => {
      const id = Number(value || 0);
      if (id > 0) ids.add(id);
    };

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      body.forEach((value, key) => {
        if (key === 'id' || /items\[\d+\]\[id\]/.test(key)) addId(value);
      });
      return ids;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      body.forEach((value, key) => {
        if (key === 'id' || /items\[\d+\]\[id\]/.test(key)) addId(value);
      });
      return ids;
    }

    if (typeof body === 'string') {
      const trimmed = body.trim();

      try {
        const json = JSON.parse(trimmed);
        if (json && typeof json === 'object') {
          addId(json.id);
          if (Array.isArray(json.items)) {
            json.items.forEach((item) => addId(item && item.id));
          }
        }
      } catch (error) {
        try {
          const params = new URLSearchParams(trimmed);
          params.forEach((value, key) => {
            if (key === 'id' || /items\[\d+\]\[id\]/.test(key)) addId(value);
          });
        } catch (paramsError) {
          /* do nothing */
        }
      }

      return ids;
    }

    if (typeof body === 'object') {
      addId(body.id);
      if (Array.isArray(body.items)) body.items.forEach((item) => addId(item && item.id));
    }

    return ids;
  }

  shouldBlockNativeCartAdd(input, init = {}) {
    const url = this.urlFromFetchInput(input);
    if (!this.isCartAddUrl(url)) return false;

    const body = init?.body || (input && input.body) || null;
    if (this.bodyContainsBundleProperty(body)) return false;

    const pageVariantIds = this.getPageVariantIds();
    if (!pageVariantIds.size) return false;

    const requestVariantIds = this.extractVariantIdsFromCartAddBody(body);
    if (!requestVariantIds.size) return false;

    return Array.from(requestVariantIds).some((id) => pageVariantIds.has(id));
  }

  handleDocumentAtcEvent(event) {
    if (event.type === 'keydown') {
      const key = event.key || '';
      if (!['Enter', ' ', 'Spacebar'].includes(key)) return;
    }

    const trigger = this.findAtcTrigger(event.target);
    if (!trigger || !this.isInScope(trigger)) return;

    const form = this.findRelatedProductForm(trigger);
    if (!form) return;

    this.stopNativeAtc(event);

    if (this.adding) return;
    if (this.isDuplicateAtcEvent(trigger)) return;

    this.handleBundleAtc(form);
  }

  handleDocumentSubmit(event) {
    if (this.adding) {
      this.stopNativeAtc(event);
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const form = target.matches('form[action*="/cart/add"]')
      ? target
      : target.closest('form[action*="/cart/add"]');

    if (!form || !this.isInScope(form)) return;

    this.stopNativeAtc(event);
    if (this.isDuplicateAtcEvent(form)) return;
    this.handleBundleAtc(form);
  }

  stopNativeAtc(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  handleBundleAtc(form) {
    if (this.adding) return;

    const selected = this.getSelectedCard();
    if (!selected) {
      this.showMessage('Please select a bundle first.');
      return;
    }

    this.addSelectedBundleToCart(selected, form);
  }

  toggleCard(card, options = {}) {
    if (!card) return;

    const isAlreadySelected = card.classList.contains('is-selected');

    /*
      Click selected bundle again = close it.
      Click a closed bundle = open it and close the others.
      Default page-load selection still opens normally through selectCard().
    */
    if (isAlreadySelected && !options.forceOpen) {
      this.closeCard(card, options);
      return;
    }

    this.selectCard(card, options);
  }

  closeCard(card, options = {}) {
    if (!card) return;

    card.classList.remove('is-selected');
    card.querySelector('[data-purist-body]')?.toggleAttribute('hidden', true);
    card.querySelector('[data-purist-select-offer]')?.setAttribute('aria-expanded', 'false');

    if (!options.silent) this.clearMessage();
  }

  selectCard(card, options = {}) {
    this.cards.forEach((otherCard) => {
      const selected = otherCard === card;
      otherCard.classList.toggle('is-selected', selected);
      otherCard.querySelector('[data-purist-body]')?.toggleAttribute('hidden', !selected);
      otherCard.querySelector('[data-purist-select-offer]')?.setAttribute('aria-expanded', selected ? 'true' : 'false');
    });

    if (!options.silent) this.clearMessage();
  }

  getSelectedCard() {
    return this.cards.find((card) => card.classList.contains('is-selected')) || null;
  }

  applyDefaultVariantMode(card) {
    const mode = (card.dataset.defaultVariantMode || 'first').trim();
    if (!['first', 'in_order', 'random'].includes(mode)) return;
    if (mode === 'first') return;

    const selects = Array.from(card.querySelectorAll('[data-purist-variant-select]'));

    selects.forEach((select, index) => {
      const enabledOptions = Array.from(select.options).filter((option) => !option.disabled && option.value);
      if (!enabledOptions.length) return;

      let optionToSelect = enabledOptions[0];

      if (mode === 'in_order') {
        optionToSelect = enabledOptions[index % enabledOptions.length];
      }

      if (mode === 'random') {
        optionToSelect = enabledOptions[Math.floor(Math.random() * enabledOptions.length)];
      }

      select.value = optionToSelect.value;
    });
  }

  selectedOptions(card) {
    return Array.from(card.querySelectorAll('[data-purist-variant-select]')).map((select) => {
      const option = select.options[select.selectedIndex];
      const item = select.closest('[data-purist-item]');

      return {
        id: Number(option.value),
        title: option.dataset.title || option.textContent.trim(),
        productTitle: option.dataset.productTitle || '',
        productId: Number(option.dataset.productId || select.dataset.puristSelectorProductId || item?.dataset.puristSelectorProductId || 0),
        isDiscountTarget: select.dataset.puristDiscountTarget === 'true' || item?.dataset.puristDiscountTarget === 'true',
        price: Number(option.dataset.price || 0),
        compare: Number(option.dataset.compare || option.dataset.price || 0),
        image: option.dataset.image || ''
      };
    });
  }

  updateItemImage(select) {
    const item = select.closest('[data-purist-item]');
    const option = select.options[select.selectedIndex];
    const image = item?.querySelector('[data-purist-item-image]');

    if (image && option?.dataset.image) {
      image.src = option.dataset.image;
      image.alt = option.dataset.title || option.textContent.trim();
    }
  }

  updateItemDisplay(select) {
    const item = select.closest('[data-purist-item]');
    const option = select.options[select.selectedIndex];
    if (!item || !option) return;

    this.updateItemImage(select);

    const title = option.dataset.title || option.textContent.trim();
    const titleEl = item.querySelector('[data-purist-item-title]');
    if (titleEl) titleEl.textContent = title.replace(/ - Sold out$/i, '');
  }

  parseValueLabelToCents(value) {
    if (!value) return 0;

    const cleanValue = String(value).replace(/,/g, '').trim();
    const match = cleanValue.match(/(\d+(?:\.\d{1,2})?)/);

    if (!match) return 0;

    const number = Number(match[1]);
    if (!Number.isFinite(number)) return 0;

    return Math.round(number * 100);
  }

  getGiftSaveAmount(card) {
    if (card.dataset.freeGiftEnabled !== 'true') return 0;

    const labelAmount = this.parseValueLabelToCents(card.dataset.freeGiftValueLabel || '');
    if (labelAmount > 0) return labelAmount;

    const giftPrice = Number(card.dataset.freeGiftPrice || 0);
    return giftPrice > 0 ? giftPrice : 0;
  }

  getShippingSaveAmount(card) {
    if (card.dataset.freeShippingEnabled !== 'true') return 0;

    return this.parseValueLabelToCents(card.dataset.freeShippingValueLabel || '');
  }

  getOfferMath(card) {
    const items = this.selectedOptions(card);
    const freeLast = card.dataset.freeLastItem === 'true';
    const percentEnabled = card.dataset.percentEnabled === 'true';
    const percent = Number(card.dataset.percentDiscount || 0);
    const includeGiftCompare = card.dataset.includeGiftCompare === 'true';
    const giftEnabled = card.dataset.freeGiftEnabled === 'true';
    const giftPrice = Number(card.dataset.freeGiftPrice || 0);

    const originalSubtotal = items.reduce((sum, item) => sum + item.price, 0);
    let total = originalSubtotal;
    /*
      Regular price = the price before the offer. The variant compare-at is deliberately ignored
      so the struck total, the per-line strikethrough, "Save" and the cart use the same baseline.
    */
    let compare = items.reduce((sum, item) => sum + item.price, 0);
    const lineAdjustments = items.map(() => ({ label: '', discountAmount: 0, role: 'paid_item', percent: 0 }));

    if (freeLast && items.length > 0) {
      const targetIndex = items.length - 1;
      const amount = items[targetIndex].price;
      total -= amount;
      lineAdjustments[targetIndex] = { label: 'FREE', discountAmount: amount, role: 'free_item', percent: 100 };
    }

    if (percentEnabled && percent > 0) {
      const targetIndex = freeLast ? items.length - 2 : items.length - 1;
      if (targetIndex >= 0 && items[targetIndex]) {
        const amount = Math.round(items[targetIndex].price * (percent / 100));
        total -= amount;
        lineAdjustments[targetIndex] = {
          label: `${percent}% Off`,
          discountAmount: amount,
          role: 'discounted_item',
          percent
        };
      }
    }

    const productDiscountSaveAmount = lineAdjustments.reduce((sum, adjustment) => {
      return sum + Number(adjustment.discountAmount || 0);
    }, 0);

    const giftSaveAmount = this.getGiftSaveAmount(card);
    const shippingSaveAmount = this.getShippingSaveAmount(card);
    const totalSaveAmount = productDiscountSaveAmount + giftSaveAmount + shippingSaveAmount;

    if (giftEnabled && includeGiftCompare) compare += giftPrice;

    return {
      items,
      total: Math.max(total, 0),
      compare: Math.max(compare, 0),
      originalSubtotal: Math.max(originalSubtotal, 0),
      productDiscountSaveAmount: Math.max(productDiscountSaveAmount, 0),
      giftSaveAmount: Math.max(giftSaveAmount, 0),
      shippingSaveAmount: Math.max(shippingSaveAmount, 0),
      totalSaveAmount: Math.max(totalSaveAmount, 0),
      lineAdjustments
    };
  }

  updateCardTotals(card) {
    const math = this.getOfferMath(card);
    const override = (card.dataset.compareOverride || '').trim();
    const totalEl = card.querySelector('[data-purist-total-price]');
    const compareEl = card.querySelector('[data-purist-compare-price]');
    const saveEl = card.querySelector('[data-purist-save]');
    const saveAmountEl = card.querySelector('[data-purist-save-amount]');
    const saveAmount = Number(math.totalSaveAmount || 0);

    if (totalEl) totalEl.textContent = this.formatMoney(math.total);

    if (compareEl) {
      if (override) {
        compareEl.textContent = override;
        compareEl.hidden = false;
      } else if (math.compare > math.total) {
        compareEl.textContent = this.formatMoney(math.compare);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (saveEl) {
      if (saveAmount > 0) {
        if (saveAmountEl) saveAmountEl.textContent = this.formatMoney(saveAmount);
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }

    card.querySelectorAll('[data-purist-item]').forEach((itemEl, index) => {
      const item = math.items[index];
      const adjustment = math.lineAdjustments[index] || {};
      if (!item) return;

      const priceEl = itemEl.querySelector('[data-purist-line-price]');
      const badgeEl = itemEl.querySelector('[data-purist-line-badge]');
      const lineSaveEl = itemEl.querySelector('[data-purist-line-save]');
      const select = itemEl.querySelector('[data-purist-variant-select]');

      if (select) this.updateItemDisplay(select);

      const hasDiscount = Number(adjustment.discountAmount || 0) > 0;
      itemEl.classList.toggle('has-adjustment', hasDiscount);

      if (priceEl) {
        if (hasDiscount) {
          priceEl.innerHTML = `<span class="purist-bundle__line-current">${this.formatMoney(item.price - adjustment.discountAmount)}</span><s>${this.formatMoney(item.price)}</s>`;
        } else {
          priceEl.textContent = this.formatMoney(item.price);
        }
      }

      if (badgeEl) {
        if (hasDiscount) {
          badgeEl.textContent = adjustment.label || (adjustment.percent ? `${adjustment.percent}% OFF` : 'SAVE');
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }

      if (lineSaveEl) {
        if (hasDiscount) {
          lineSaveEl.textContent = `You Save ${this.formatMoney(adjustment.discountAmount)}`;
          lineSaveEl.hidden = false;
        } else {
          lineSaveEl.hidden = true;
        }
      }
    });
  }

  async addSelectedBundleToCart(card, form) {
    const math = this.getOfferMath(card);
    const bundleId = `purist-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const offerTitle = card.dataset.offerTitle || 'Bundle offer';
    const discountCode = card.dataset.discountCode || '';

    const bundleLines = math.items.map((item, index) => {
      const adjustment = math.lineAdjustments[index] || {};
      const role = adjustment.role || 'paid_item';

      return {
        id: item.id,
        quantity: 1,
        properties: this.cleanProperties({
          _purist_bundle_id: bundleId,
          _purist_bundle_offer: offerTitle,
          _purist_bundle_role: role,
          _purist_bundle_position: String(index + 1),
          _purist_bundle_selector_product_id: item.productId ? String(item.productId) : '',
          _purist_bundle_discount_target: item.isDiscountTarget ? 'true' : '',
          _purist_bundle_discount_percent: adjustment.percent ? String(adjustment.percent) : '',
          _purist_bundle_discount_code: discountCode,
          /* Numbers the cart drawer displays, so PDP card and cart always agree. */
          _purist_bundle_regular_price: String(Math.round(Number(item.price || 0))),
          _purist_bundle_gift_value: String(Math.round(Number(math.giftSaveAmount || 0))),
          _purist_bundle_shipping_value: String(Math.round(Number(math.shippingSaveAmount || 0)))
        })
      };
    });

    /*
      Shopify native Buy X Get Y discounts decide which eligible line receives the
      discount. When all bundle variants are from the same product, the safest
      code-side way to make the chosen “free/discounted” dropdown item win is to
      add that intended target line before the other paid bundle lines.

      The private _purist_bundle_position property keeps the original selector
      order and also prevents Shopify from merging a paid and free copy of the
      same variant into one cart line.
    */
    const priorityRoles = ['free_item', 'discounted_item'];
    const items = bundleLines.sort((lineA, lineB) => {
      const roleA = lineA.properties?._purist_bundle_role || 'paid_item';
      const roleB = lineB.properties?._purist_bundle_role || 'paid_item';
      const priorityA = priorityRoles.includes(roleA) ? 0 : 1;
      const priorityB = priorityRoles.includes(roleB) ? 0 : 1;

      if (priorityA !== priorityB) return priorityA - priorityB;

      const positionA = Number(lineA.properties?._purist_bundle_position || 0);
      const positionB = Number(lineB.properties?._purist_bundle_position || 0);
      return positionA - positionB;
    });

    if (card.dataset.freeGiftEnabled === 'true' && Number(card.dataset.freeGiftVariantId || 0) > 0) {
      items.push({
        id: Number(card.dataset.freeGiftVariantId),
        quantity: 1,
        properties: this.cleanProperties({
          _purist_bundle_id: bundleId,
          _purist_bundle_offer: offerTitle,
          _purist_bundle_role: 'free_gift',
          _purist_bundle_discount_percent: '100',
          _purist_bundle_discount_code: discountCode,
          _purist_bundle_gift_value: String(Math.round(Number(math.giftSaveAmount || 0))),
          _purist_bundle_shipping_value: String(Math.round(Number(math.shippingSaveAmount || 0)))
        })
      });
    }

    if (!items.length) return;

    this.adding = true;
    this.setLoading(true);
    this.clearMessage();

    try {
      const payload = { items };

      if (this.cartSections.length) {
        payload.sections = this.cartSections;
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const state = await response.json();

      if (!response.ok) {
        throw new Error(state.description || state.message || 'Unable to add bundle to cart.');
      }

      let cart = await this.fetchCart();
      cart = (await this.removeRogueOriginalProductLines(cart)) || cart;

      this.renderCartSections(state.sections || {});
      this.updateCartCount(cart);
      this.dispatchCartEvents(cart, state, items);

      setTimeout(async () => {
        const cleanedCart = await this.removeRogueOriginalProductLines();
        if (cleanedCart) {
          this.updateCartCount(cleanedCart);
          this.dispatchCartEvents(cleanedCart, state, items);
        }
      }, 700);

      if (window.PuristGiftGuard && typeof window.PuristGiftGuard.scheduleFastGuard === 'function') {
        window.PuristGiftGuard.scheduleFastGuard();
      }

      this.openCartDrawer();

      form?.dispatchEvent(new CustomEvent('purist:bundle-added', {
        bubbles: true,
        detail: { cart, response: state, items }
      }));
    } catch (error) {
      this.showMessage(error.message || 'Unable to add bundle to cart. Please try again.');
    } finally {
      this.adding = false;
      this.setLoading(false);
    }
  }


  cartItemHasPuristBundle(item) {
    return Boolean(item && item.properties && item.properties._purist_bundle_id);
  }

  async removeRogueOriginalProductLines(cart = null) {
    const pageProductId = this.getPageProductId();
    if (!pageProductId) return cart;

    const currentCart = cart || (await this.fetchCart());
    if (!currentCart || !Array.isArray(currentCart.items)) return cart;

    const updates = {};

    currentCart.items.forEach((item) => {
      if (!item || !item.key) return;
      if (Number(item.product_id || 0) !== pageProductId) return;
      if (this.cartItemHasPuristBundle(item)) return;

      updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return currentCart;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) return currentCart;
      return await response.json();
    } catch (error) {
      return currentCart;
    }
  }

  async fetchCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  renderCartSections(sections) {
    Object.entries(sections).forEach(([sectionId, html]) => {
      if (!html) return;

      // The cart drawer is never patched from here: OCU owns its own drawer, and the native
      // theme drawer re-renders itself from /cart?section_id=cart-drawer (see openCartDrawer).
      if (/cart-drawer|mini-cart|cart-notification/i.test(sectionId)) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');

      if (window.CSS && CSS.escape) {
        const incomingShopifySection = parsed.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);
        const currentShopifySection = document.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);

        if (incomingShopifySection && currentShopifySection) {
          currentShopifySection.innerHTML = incomingShopifySection.innerHTML;
        }
      }
    });
  }

  updateCartCount(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    const count = Number(cart.item_count || 0);
    const countSelectors = [
      '.m-cart-count-bubble',
      '.m-cart-count',
      '[data-cart-count]',
      '.cart-count-bubble',
      '.cart-count',
      '[data-testid="cart-count"]',
      '.ocu-cart-header--items-count span'
    ];

    countSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((countEl) => {
        countEl.textContent = String(count);

        if (count > 0) {
          countEl.classList.remove('m:hidden');
          countEl.removeAttribute('hidden');
          countEl.style.removeProperty('display');
        } else {
          countEl.classList.add('m:hidden');
        }
      });
    });

    document.querySelectorAll('.m-cart-icon-bubble[aria-label], [aria-label][href*="/cart"]').forEach((cartIcon) => {
      cartIcon.setAttribute('aria-label', String(count));
    });
  }

  dispatchCartEvents(cart, state, items) {
    const detail = { cart, response: state, items };

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail }));

    /* App-cart compatibility events. These are harmless if the app does not listen to them. */
    document.dispatchEvent(new CustomEvent('ocu:cart:refresh', { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent('ocu:cart:refresh', { detail }));

    /* Native theme drawer (cart-drawer.js) re-renders itself on this one. */
    document.dispatchEvent(new CustomEvent('purist:cart:refresh', { bubbles: true, detail }));

    if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
      window.Shopify.onCartUpdate(cart);
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents) {
      try {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, state);
        // Do NOT emit MinimogTheme.pubSubEvents.openCartDrawer here.
        // That event opens the theme/native cart drawer. OCU should remain the only drawer.
      } catch (error) {
        /* Do nothing. */
      }
    }
  }

  /*
    Which drawer is on the page?
      - OCU installed  → `.ocu-cart-drawer` (or an OCU trigger) exists → OCU path, unchanged.
      - OCU removed    → the theme's own <m-cart-drawer data-purist-native-cart> is used:
                         cart-drawer.js exposes refreshAndOpen(), which re-fetches the
                         drawer section (so the bundle card renders) and slides it open.
    Nothing about how the bundle is added changes; this only decides what opens afterwards.
  */
  ocuCartPresent() {
    return Boolean(
      document.querySelector(
        '.ocu-cart-drawer, [data-ocu-cart-trigger], [data-open-ocu-cart], .ocu-cart-trigger, .ocu-cart-icon'
      )
    );
  }

  openNativeCartDrawer() {
    const api = window.PuristCartDrawer;
    if (api && typeof api.isAvailable === 'function' && api.isAvailable()) {
      api.refreshAndOpen();
      return true;
    }

    const drawer = document.querySelector('m-cart-drawer[data-purist-native-cart], m-cart-drawer');
    if (drawer && typeof drawer.refreshAndOpen === 'function') {
      drawer.refreshAndOpen();
      return true;
    }

    if (drawer && typeof drawer.open === 'function') {
      if (typeof drawer.refresh === 'function') drawer.refresh();
      drawer.open();
      return true;
    }

    return false;
  }

  openCartDrawer() {
    if (!this.ocuCartPresent() && this.openNativeCartDrawer()) return;

    const customSelector = (this.dataset.openDrawerSelector || '').trim();

    // Custom selector is allowed only for OCU/app cart targets.
    // Do not click native theme cart triggers such as [aria-controls="cart-drawer"].
    if (customSelector) {
      const customTarget = document.querySelector(customSelector);
      if (customTarget) {
        const isOcuTarget = customTarget.matches('.ocu-cart-drawer, [data-ocu-cart-trigger], [data-open-ocu-cart], .ocu-cart-trigger, .ocu-cart-icon') || customTarget.closest('.ocu-cart-drawer');
        if (isOcuTarget) {
          if (customTarget.matches('button, a, [role="button"], summary')) {
            customTarget.click();
          } else {
            this.forceOpenOcuDrawer(customTarget.closest('.ocu-cart-drawer') || customTarget);
          }
        }
        return;
      }
    }

    const ocuClickSelectors = [
      '[data-ocu-cart-trigger]',
      '[data-open-ocu-cart]',
      '.ocu-cart-trigger',
      '.ocu-cart-icon'
    ];

    for (const selector of ocuClickSelectors) {
      const trigger = document.querySelector(selector);
      if (trigger) {
        trigger.click();
        return;
      }
    }

    const ocuDrawer = document.querySelector('.ocu-cart-drawer');
    if (ocuDrawer) this.forceOpenOcuDrawer(ocuDrawer);
  }

  forceOpenOcuDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('ocu-cart-drawer')) return;

    drawer.removeAttribute('hidden');
    drawer.removeAttribute('aria-hidden');
    drawer.setAttribute('open', '');
    drawer.classList.add('ocu-visible');
    drawer.classList.remove('ocu-hidden');

    document.documentElement.classList.add('ocu-cart-open');
    document.body.classList.add('ocu-cart-open');

    // OCU/app-only events. Do not dispatch native theme drawer events.
    document.dispatchEvent(new CustomEvent('ocu:cart:open', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('ocu:cart:open'));
  }

  cleanProperties(properties) {
    return Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }

  formatMoney(cents) {
    cents = Number(cents || 0);
    const value = (cents / 100).toFixed(2);

    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);

    const scope = this.getScope();
    const buttons = new Set([
      ...Array.from(scope.querySelectorAll('[data-purist-bundle-atc="true"], .m-add-to-cart[name="add"]')),
      ...Array.from(document.querySelectorAll(`${this.stickyAtcSelector()} [data-purist-bundle-atc="true"], ${this.stickyAtcSelector()} .m-add-to-cart[name="add"]`))
    ]);

    buttons.forEach((button) => {
      button.toggleAttribute('aria-busy', loading);
      button.classList.toggle('m-spinner-loading', loading);
    });
  }

  showMessage(message) {
    if (this.message) this.message.textContent = message;
  }

  clearMessage() {
    if (this.message) this.message.textContent = '';
  }
}

if (!customElements.get('purist-bundle-offers')) {
  customElements.define('purist-bundle-offers', PuristBundleOffers);
}
